import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router";

/** Props Interface */
interface UseLeaveGuardProps {
	/** Whether leaving now would lose something — a form's `isDirty`, typically. */
	isDirty: boolean;
	/** Asks the author whether to leave anyway. Resolves to true to let the navigation through. */
	confirmLeave: () => Promise<boolean>;
}

/**
 * Holds the page while it carries unsaved changes. Navigating within the app — a link, the
 * sidebar, a `navigate()`, the back button — is stopped and put to the author first. Closing or
 * reloading the tab gets the browser's own prompt, which is the most a page is allowed there:
 * browsers show their own wording, and only once the author has interacted with the page.
 *
 * `release()` stands the guard down for the navigation that follows a save or a confirmed
 * discard. It is a ref rather than state on purpose: the caller navigates in the same tick, and
 * the router asks the blocker at that moment, not after the next render.
 */
export function useLeaveGuard({ isDirty, confirmLeave }: UseLeaveGuardProps) {
	/** Hooks */
	const isDirtyRef = useRef(isDirty);
	const isReleasedRef = useRef(false);
	const shouldBlock = useCallback(() => isDirtyRef.current && !isReleasedRef.current, []);
	const blocker = useBlocker(shouldBlock);

	/** Effects */
	useEffect(() => {
		isDirtyRef.current = isDirty;
	}, [isDirty]);

	// Keyed on the blocker itself, not on its state: the router hands out a new blocker for every
	// navigation it stops, and it renders those in transitions, so a decline followed at once by
	// another stopped click can skip the "unblocked" render — two blocks, one state string, and an
	// effect keyed on the string would not ask the second time.
	useEffect(() => {
		if (blocker.state !== "blocked") return;
		let isCurrent = true;
		confirmLeave().then((isConfirmed) => {
			if (!isCurrent) return;
			if (isConfirmed) blocker.proceed();
			else blocker.reset();
		});
		return () => {
			isCurrent = false;
		};
	}, [blocker]);

	useEffect(() => {
		if (!isDirty) return;
		const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [isDirty]);

	/** Functions */
	const release = useCallback(() => {
		isReleasedRef.current = true;
	}, []);

	/** Return */
	return { release };
}
